import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/roleLabels";
import { Toaster } from "sonner";
import {
  ChartBarIcon,
  StackIcon,
  FolderIcon,
  CheckSquareIcon,
  UsersThreeIcon,
  FileTextIcon,
  SignOutIcon,
  SparkleIcon,
  CalendarIcon,
  EnvelopeIcon,
  ChatCircleDotsIcon,
  WarningCircleIcon,
  GlobeHemisphereWestIcon,
  KeyIcon,
  SidebarSimpleIcon,
} from "@phosphor-icons/react";

const SIDEBAR_COLLAPSED_KEY = "trk-sidebar-collapsed";

const NAV = [
  { to: "/", label: "Dashboard", icon: ChartBarIcon, testid: "nav-dashboard" },
  { to: "/initiatives", label: "Initiatives", icon: StackIcon, testid: "nav-initiatives" },
  { to: "/projects", label: "Projects", icon: FolderIcon, testid: "nav-projects" },
  { to: "/tasks", label: "Tasks", icon: CheckSquareIcon, testid: "nav-tasks" },
  { to: "/issues", label: "Issues", icon: WarningCircleIcon, testid: "nav-issues" },
  { to: "/team", label: "Team", icon: UsersThreeIcon, testid: "nav-team" },
  { to: "/skills", label: "Skills", icon: SparkleIcon, testid: "nav-skills" },
  { to: "/leaves", label: "Leaves", icon: CalendarIcon, testid: "nav-leaves" },
  { to: "/reports", label: "Reports", icon: FileTextIcon, testid: "nav-reports" },
  { to: "/digest", label: "Digest", icon: EnvelopeIcon, testid: "nav-digest" },
  { to: "/chat", label: "Ask AI", icon: ChatCircleDotsIcon, testid: "nav-chat" },
];

// One easing/duration for every moving part so width, labels, the toggle and
// the footer all travel together instead of snapping at different moments.
const EASE = "duration-200 ease-out";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const nav = useNavigate();

  // Persisted per-browser (not per-account — a shared machine keeps
  // whichever state was last set) so the choice survives navigation and
  // reloads instead of resetting to expanded every page.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      // Private browsing / storage disabled — collapse state just won't
      // persist across reloads, not worth surfacing to the user.
    }
  }, [collapsed]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const allNav = [...NAV, ...(user?.role === "admin" ? [{
    to: "/settings/digital-portal",
    label: "Digital Portal",
    icon: GlobeHemisphereWestIcon,
    testid: "nav-digital-portal",
  }] : [])];

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar. Every element stays mounted in both states and only its
          size/opacity/position changes, so collapsing and expanding animate
          instead of swapping two different layouts. */}
      <aside
        className={`${collapsed ? "w-[72px]" : "w-64"} shrink-0 overflow-hidden border-r border-border bg-white flex flex-col sticky top-0 h-screen will-change-[width] transition-[width] ${EASE}`}
      >
        {/* Brand header: logo tile; panel icon toggles collapse. Expanded the
            toggle sits to the right of the logo, collapsed it slides under it. */}
        <div className={`relative shrink-0 transition-[height] ${EASE} ${collapsed ? "h-[108px]" : "h-[76px]"}`}>
          <Link
            to="/"
            data-testid="brand-link"
            title="Initiative Board"
            className="absolute left-[14px] top-4"
          >
            <img src="/initiative-logo.png" alt="Initiative Board" className="h-11 w-11 rounded-xl object-cover" />
          </Link>
          {/* Indorama wordmark fills the gap between the logo and the toggle;
              fades out with the labels when the sidebar collapses. */}
          <img
            src="/indorama-logo.png"
            alt="Indorama"
            className={`absolute left-[72px] top-[32px] w-[124px] h-auto pointer-events-none select-none transition-opacity ${EASE} ${
              collapsed ? "opacity-0" : "opacity-100"
            }`}
          />
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="sidebar-collapse-toggle"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`absolute p-2 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-[top,left,background-color,color] ${EASE} ${
              collapsed ? "left-[18px] top-[64px]" : "left-[212px] top-[22px]"
            }`}
          >
            <SidebarSimpleIcon size={20} />
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 pt-1 pb-4 space-y-0.5">
          <div
            className={`trk-uppercase-label px-3 overflow-hidden whitespace-nowrap transition-[max-height,opacity,padding] ${EASE} ${
              collapsed ? "max-h-0 opacity-0 py-0" : "max-h-8 opacity-100 pb-1 pt-1"
            }`}
            style={{ color: "#94A3B8" }}
          >
            Menu
          </div>
          {allNav.map((n) => {
            const active = pathname === n.to || (n.to !== "/" && pathname.startsWith(n.to));
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                title={collapsed ? n.label : undefined}
                className={`flex items-center rounded-xl py-2.5 pl-[14px] text-sm transition-colors duration-200 ${
                  active
                    ? "bg-[#EFF6FF] text-[#0B3D91] font-semibold"
                    : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
                }`}
              >
                <Icon size={20} weight={active ? "fill" : "regular"} className="shrink-0" />
                <span
                  className={`overflow-hidden whitespace-nowrap transition-[max-width,opacity] ${EASE} ${
                    collapsed ? "max-w-0 opacity-0" : "max-w-[180px] opacity-100"
                  }`}
                >
                  <span className="pl-3">{n.label}</span>
                </span>
              </Link>
            );
          })}
        </nav>

        <div className="shrink-0 border-t border-border relative">
          {/* Collapsed footer: icon buttons */}
          <div
            className={`flex flex-col items-center gap-2 overflow-hidden transition-[max-height,opacity,padding] ${EASE} ${
              collapsed ? "max-h-32 opacity-100 p-2" : "max-h-0 opacity-0 p-0 pointer-events-none"
            }`}
            aria-hidden={!collapsed}
          >
            <button
              onClick={handleLogout}
              data-testid={collapsed ? "logout-button" : undefined}
              tabIndex={collapsed ? 0 : -1}
              className="p-2 border border-border hover:bg-zinc-50 rounded-md transition-colors"
              title={`Log out (${user?.email})`}
            >
              <SignOutIcon size={16} />
            </button>
            <Link
              to="/account/password"
              data-testid={collapsed ? "change-password-link" : undefined}
              tabIndex={collapsed ? 0 : -1}
              title="Account settings"
              className="p-2 border border-border rounded-md hover:bg-zinc-50 hover:border-[#0176D3] hover:text-[#0176D3] transition-colors"
            >
              <KeyIcon size={14} />
            </Link>
          </div>

          {/* Expanded footer: signed-in user + account settings */}
          <div
            className={`overflow-hidden transition-[max-height,opacity,padding] ${EASE} ${
              collapsed ? "max-h-0 opacity-0 p-0 pointer-events-none" : "max-h-60 opacity-100 p-4"
            }`}
            aria-hidden={collapsed}
          >
            <div className="w-56">
              <div className="text-xs text-zinc-500 mb-2">Signed in as</div>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
                  <div className="trk-uppercase-label mt-0.5">{roleLabel(user?.role)}</div>
                </div>
                <button
                  onClick={handleLogout}
                  data-testid={collapsed ? undefined : "logout-button"}
                  tabIndex={collapsed ? -1 : 0}
                  className="p-2 border border-border hover:bg-zinc-50 rounded-md transition-colors"
                  title="Log out"
                >
                  <SignOutIcon size={16} />
                </button>
              </div>
              <Link
                to="/account/password"
                data-testid={collapsed ? undefined : "change-password-link"}
                tabIndex={collapsed ? -1 : 0}
                className="mt-3 flex items-center justify-center gap-2 text-xs font-medium border border-border rounded-md py-2 hover:bg-zinc-50 hover:border-[#0176D3] hover:text-[#0176D3] transition-colors"
              >
                <KeyIcon size={13} /> Account settings
              </Link>
              {/* Two-factor auth entry point is hidden while the feature is
                  disabled app-wide — see TWO_FA_ENABLED in AuthContext.jsx. */}
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Admin 2FA enforcement banner removed — the feature is hidden
            app-wide for now (see TWO_FA_ENABLED in AuthContext.jsx). */}
        {children}
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
